import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { LayoutGrid, FolderClosed, LineChart as LineIcon, MessageCircle, Settings as SettingsIcon, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SettingsProvider, useClientSettings } from '../client-pwa/settings';
import SplashV2 from './SplashV2';

function ShellV2() {
  const { settings } = useClientSettings();
  const location = useLocation();
  const navItems = [
    { to: '/client-pwa-v2', icon: LayoutGrid, label: 'Overview', end: true },
    { to: '/client-pwa-v2/files', icon: FolderClosed, label: 'Vault' },
    { to: '/client-pwa-v2/insights', icon: LineIcon, label: 'Pulse' },
    { to: '/client-pwa-v2/help', icon: MessageCircle, label: 'Concierge' },
  ];

  const onSettingsPage = location.pathname.includes('/settings');

  return (
    <div
      className={cn(
        'relative min-h-screen overflow-hidden bg-[#2a2a3a] text-white',
        settings.largeText && 'text-[110%]',
      )}
    >
      {/* Animated mesh background */}
      <div aria-hidden className="pointer-events-none fixed inset-0 overflow-hidden opacity-60">
        <div className="absolute -top-1/4 -left-1/4 h-[600px] w-[600px] rounded-full bg-violet-600/30 blur-[120px]" />
        <div className="absolute top-1/3 -right-1/4 h-[500px] w-[500px] rounded-full bg-fuchsia-600/20 blur-[100px]" />
        <div className="absolute bottom-0 left-1/3 h-[400px] w-[400px] rounded-full bg-cyan-500/15 blur-[110px]" />
      </div>

      <SplashV2 />

      <div className="relative mx-auto flex min-h-screen w-full max-w-[640px] flex-col">
        {!onSettingsPage && (
          <header className="flex items-center justify-between px-5 pt-6 pb-2">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-amber-400 text-sm font-bold tracking-tight shadow-lg shadow-violet-500/30">
                  BA
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#2a2a3a] bg-emerald-400" />
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/40">
                  James Tiling
                </p>
                <h1 className="text-base font-semibold tracking-tight">James Halloran</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/70 backdrop-blur-md transition-colors hover:bg-white/10">
                <Bell className="h-4 w-4" />
                <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-fuchsia-400" />
              </button>
              <Link
                to="/client-pwa-v2/settings"
                className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/70 backdrop-blur-md transition-colors hover:bg-white/10"
              >
                <SettingsIcon className="h-4 w-4" />
              </Link>
            </div>
          </header>
        )}

        <main className="flex-1 px-5 pb-32">
          <Outlet />
        </main>

        {/* Floating glass nav */}
        <nav className="fixed bottom-4 left-1/2 z-30 w-[calc(100%-2rem)] max-w-[440px] -translate-x-1/2">
          <div className="rounded-full border border-white/10 bg-white/5 p-1.5 shadow-2xl shadow-black/40 backdrop-blur-2xl">
            <div className="flex items-center justify-around">
              {navItems.map(({ to, icon: Icon, label, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      'relative flex flex-1 flex-col items-center gap-0.5 rounded-full px-3 py-2 transition-all',
                      isActive
                        ? 'bg-gradient-to-br from-violet-500/30 to-fuchsia-500/20 text-white'
                        : 'text-white/50 hover:text-white/80'
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className={cn('h-4 w-4', isActive && 'stroke-[2.5]')} />
                      <span className={cn('text-[10px] tracking-wide', isActive && 'font-semibold')}>{label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
}

export default function ClientPwaV2Layout() {
  return (
    <SettingsProvider>
      <ShellV2 />
    </SettingsProvider>
  );
}
